/**
 * TaskList - Página de listagem paginada de tarefas
 * 
 * Layout padronizado com CrmDeals:
 * - Header com refresh + timestamp + métricas inline
 * - Barra de filtros premium com bg-muted/30
 * - Tabela com headers ordenáveis (ícones), linhas clicáveis
 * - Coluna de ações com DropdownMenu contextual
 * - PaginationFooter reutilizável
 * - Skeleton loading + empty state premium
 * - SmartAvatar para responsáveis
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-07 v2.0.0 - Padronização com layout CrmDeals
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { useTaskModule } from '@/hooks/useTaskModuleType';
import { getTaskModuleMenuKeys } from '@/lib/tasks/taskModuleMenuKeys';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { 
  Search, 
  X, 
  Plus, 
  AlertTriangle, 
  MessageSquare, 
  ListChecks,
  ArrowUpDown,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Clock,
  Eye,
  Pencil,
  ClipboardList,
  Filter,
  Tag,
  CheckCircle2,
  PauseCircle,
  XCircle,
  RotateCcw,
  PlayCircle,
  Kanban,
  SlidersHorizontal,
  CalendarDays,
  Calendar as CalendarIcon,
  User,
  LayoutList,
  Rows3,
  ChevronsDownUp,
  ChevronsUpDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { RETENTION_PERIOD_OPTIONS } from '@/lib/tasks/retention-period';
import { ptBR } from 'date-fns/locale';

import { TaskFormDialog } from '@/components/tasks/modal/TaskFormDialog';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';
import { TaskCreatedSuccessDialog } from '@/components/tasks/modal/TaskCreatedSuccessDialog';
import { TaskChangePhaseDialog } from '@/components/tasks/modal/TaskChangePhaseDialog';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { useTaskClassifications } from '@/hooks/useTaskClassifications';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTasksPaginated, type TaskListFilters, type TaskListItem } from '@/hooks/useTasksPaginated';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { useTaskListKpis } from '@/hooks/useTaskListKpis';
import { useTaskListRealtime } from '@/hooks/useTaskListRealtime';
import { TaskListKpiCards } from '@/components/tasks/TaskListKpiCards';
import { TaskBulkEditBar } from '@/components/tasks/TaskBulkEditBar';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { CrmUserFilter } from '@/components/crm/filters/CrmUserFilter';
import { Checkbox } from '@/components/ui/checkbox';
import { createLogger } from '@/lib/logger';
import { formatPauseTimer } from '@/lib/utils/pause-helpers';
import { TaskTimerCell } from '@/components/tasks/TaskTimerCell';
import { DueDateQuickFilter, type DueDatePreset } from '@/components/tasks/filters/DueDateQuickFilter';
import { TasksUsageInline } from '@/components/tasks/billing/TasksUsageInline';
import { LimitReachedModal } from '@/components/tenant/LimitReachedModal';
import { useTasksUsage } from '@/hooks/useTasksUsage';
import { TaskGroupedView, type TaskGroupBy } from '@/components/tasks/list/TaskGroupedView';
import {
  TaskListTable,
  TaskListTableProvider,
  type TaskListTableContextValue,
} from '@/components/tasks/list/TaskListTable';
import { useTasksListViewPreference } from '@/hooks/useTasksListViewPreference';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';

/** Logger do módulo */
const logger = createLogger('TaskList');

/* =========================================================================
 * Constantes e PhaseDropdown ficam em `taskListShared.tsx` para que possam
 * ser reusados pela visão agrupada (TaskGroupedView) sem dependência
 * circular. Importamos uma vez e reexportamos para manter compatibilidade
 * com quem já importa de `@/pages/tasks/TaskList`.
 * ========================================================================= */
import {
  PRIORITY_BAR_COLORS as SHARED_PRIORITY_BAR_COLORS,
  STATUS_LABELS as SHARED_STATUS_LABELS,
  STATUS_STYLES as SHARED_STATUS_STYLES,
  PhaseDropdown as SHARED_PhaseDropdown,
  formatDue as SHARED_formatDue,
} from '@/components/tasks/list/taskListShared';
export const PRIORITY_BAR_COLORS = SHARED_PRIORITY_BAR_COLORS;
export const STATUS_LABELS = SHARED_STATUS_LABELS;
export const STATUS_STYLES = SHARED_STATUS_STYLES;
export const PhaseDropdown = SHARED_PhaseDropdown;
export const formatTaskDue = SHARED_formatDue;




/**
 * DateFilterRow — Linha de filtro de data (De/Até) para o Popover "Mais"
 * Padrão reutilizado do CRM Deals
 */
interface DateFilterRowProps {
  label: string;
  fromDate: Date | undefined;
  toDate: Date | undefined;
  onFromChange: (date: Date | undefined) => void;
  onToChange: (date: Date | undefined) => void;
}

function DateFilterRow({ label, fromDate, toDate, onFromChange, onToChange }: DateFilterRowProps) {
  return (
    <div className="space-y-1.5">
      {/* Label com ícone pequeno e cor foreground (padrão Kanban) */}
      <Label className="text-xs flex items-center gap-1.5">
        <CalendarDays className="h-3 w-3" />
        {label}
      </Label>
      <div className="flex gap-2">
        {/* Date picker "De" */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
              {fromDate ? format(fromDate, 'dd/MM/yy') : 'De'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" side="bottom">
            <Calendar
              mode="single"
              selected={fromDate}
              onSelect={onFromChange}
              className="p-3 pointer-events-auto"
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
        {/* Date picker "Até" */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
              {toDate ? format(toDate, 'dd/MM/yy') : 'Até'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" side="bottom">
            <Calendar
              mode="single"
              selected={toDate}
              onSelect={onToChange}
              className="p-3 pointer-events-auto"
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}


/* PhaseDropdown foi extraído para `taskListShared.tsx` (reexportado acima). */


/**
 * Props opcionais — usadas quando TaskList é renderizado dentro de um modal
 * (drill-down dos KPIs do Dashboard de Tarefas) para pré-aplicar e travar filtros.
 */
export interface TaskListProps {
  /** Filtros iniciais aplicados ao carregar (usuário pode alterá-los). */
  initialFilters?: Partial<TaskListFilters>;
  /** Filtros travados — sempre mesclados nas RPCs e não removíveis pela UI. */
  lockedFilters?: Partial<TaskListFilters>;
  /** Oculta o header da página (título "Tarefas" + busca + botões da direita). */
  hideHeader?: boolean;
  /** Oculta os cards de KPI totalizadores. */
  hideKpiCards?: boolean;
  /** Título customizado (substitui "Tarefas"). */
  title?: string;
  /** Subtítulo customizado. */
  subtitle?: string;
  /**
   * Força o modo de visualização "lista" ignorando a preferência persistida
   * em localStorage. Usado no drill-down dos KPIs (TasksListModal) para que o
   * modal sempre apresente a tabela padrão, independente do modo agrupado
   * eventualmente escolhido pelo usuário na página de menu.
   */
  forceListView?: boolean;
}

export default function TaskList({
  initialFilters,
  lockedFilters,
  hideHeader = false,
  hideKpiCards = false,
  title,
  subtitle,
  forceListView = false,
}: TaskListProps = {}) {
  const navigate = useNavigate();
  // v: módulo corrente (normal | retention | legal) — define rótulos e rotas dos atalhos
  const taskModule = useTaskModule();
  const queryClient = useQueryClient();
  const { user, currentTenant } = useAuth();
  const { canEdit, canCreate } = usePermissions();

  // v3.2.0: termo vindo da URL (?q=) — usado pelos atalhos "Ver todos em ..."
  // da pesquisa global. Lido apenas na montagem (estado inicial).
  const [urlSearchParams] = useSearchParams();
  const initialUrlQuery = urlSearchParams.get('q')?.trim() || '';

  // Estado dos filtros — inicializa com initialFilters quando fornecido
  const [filters, setFilters] = useState<TaskListFilters>(() => ({
    ...(initialFilters || {}),
    ...(initialUrlQuery ? { search: initialUrlQuery } : {}),
  }));

  // Inicializa "Incluir finalizadas" respeitando o que vier do drill-down do KPI
  // (initialFilters/lockedFilters). Antes ficava sempre false e sobrescrevia o
  // include_completed:true do preset (ex.: Criadas Hoje), causando lista vazia.
  const [includeCompleted, setIncludeCompleted] = useState<boolean>(
    () => Boolean(initialFilters?.include_completed || lockedFilters?.include_completed)
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Estado do dialog de criação
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Quick Create (?new=1): abre o dialog "Nova tarefa".
  useAutoOpenFromQuery('1', () => setShowCreateDialog(true));

  // Estado do modal "limite mensal de tarefas atingido" — disparado pelo trigger do banco
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  // Snapshot atual do uso para popular o modal com contagem real
  const { data: tasksUsage } = useTasksUsage();
  
  // Estado do modal de detalhes
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Estado do modal de sucesso após criação
  const [createdTaskId, setCreatedTaskId] = useState<string | null>(null);
  const [createdTaskNumber, setCreatedTaskNumber] = useState<number>(0);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // Estado do dialog de edição
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  // Tarefa alvo do diálogo "Alterar Fase" aberto pela listagem (null = fechado)
  const [phaseDialogTask, setPhaseDialogTask] = useState<TaskListItem | null>(null);

  // Busca de texto
  const [searchInput, setSearchInput] = useState(initialUrlQuery);

  /** Estado de expand/collapse para hierarquia de subtarefas */
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [subtasksMap, setSubtasksMap] = useState<Record<string, TaskListItem[]>>({});
  const [loadingSubtasks, setLoadingSubtasks] = useState<Set<string>>(new Set());

  // v1.1.0: Estado de edição em massa
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  // 2026-10-22: chaves do próprio módulo (Retenção/Jurídico separados de Tarefas)
  const moduleKeys = getTaskModuleMenuKeys(taskModule.type);
  const canBulkEdit = canEdit(moduleKeys.list);

  // v3.0.0: Modo de visualização (lista padrão x lista agrupada por campo)
  // Reusa 100% dos filtros, KPIs, permissões e modais da página.
  // v3.1.0: Preferência de visualização persistida em localStorage
  // (sobrevive a navegações/reload). Padrões: lista + por fase.
  const { viewMode: persistedViewMode, setViewMode, groupBy, setGroupBy, groupsDefault, setGroupsDefault } =
    useTasksListViewPreference();
  /** v3.2.0: quando forceListView é true (modal de KPIs), ignora preferência persistida. */
  const viewMode = forceListView ? 'list' : persistedViewMode;
  // Contador que dispara comando em massa de expandir/colapsar nos grupos.
  const [expandAllSignal, setExpandAllSignal] = useState(0);

  // Hook de refresh com timestamp (padrão CrmDeals)
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();

  // Hook de dados — v1.15.0: passa include_completed via filtros + lockedFilters mesclados (drill-down)
  const mergedFilters = useMemo(() => ({
    ...filters,
    ...(lockedFilters || {}),
    include_completed: includeCompleted,
  }), [filters, includeCompleted, lockedFilters]);

  const { tasks, totalRecords, isLoading } = useTasksPaginated(
    mergedFilters, page, pageSize, sortBy, sortDir
  );

  // Categorias para filtro
  const { categories } = useTaskCategories(true);

  // Hook de permissões granulares por fase (sem filtro de categoria — carrega todas as fases)
  // Usado para controle de UI (botões de ação) — filtro can_view agora é server-side nas RPCs
  const { canDoOnPhase, permissionsMap, isAdmin: isPhaseAdmin } = useTaskPhasePermissions();

  // v2.1.0: Verifica se o usuário pode criar em pelo menos uma fase (alinhado com Kanban)
  const canCreateInAnyPhase = useMemo(() => {
    if (isPhaseAdmin) return true;
    for (const [, perm] of permissionsMap) {
      if (perm.can_create) return true;
    }
    return false;
  }, [isPhaseAdmin, permissionsMap]);

  // Estado do filtro ativo via KPI card (status ou 'created_today')
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);

  /**
   * Filtros separados para o hook de KPIs — evita circularidade.
   * Quando um card KPI está ativo, exclui o filtro injetado por ele
   * para que os demais cards mantenham seus totais globais.
   * Inclui lockedFilters para refletir o contexto do drill-down.
   */
  const kpiFilters = useMemo(() => {
    const f = { ...filters, ...(lockedFilters || {}) };
    if (kpiFilter === 'created_today') {
      delete f.date_from;
      delete f.date_to;
    } else if (kpiFilter) {
      delete f.status;
    }
    return f;
  }, [filters, kpiFilter, lockedFilters]);

  // Hook de KPIs totalizadores (server-side com permissões granulares)
  const { kpis, isLoading: kpisLoading } = useTaskListKpis(kpiFilters);

  // v1.12.0: Estados de filtros de data avançados (botão "Mais")
  const [dueDateFrom, setDueDateFrom] = useState<Date | undefined>();
  const [dueDateTo, setDueDateTo] = useState<Date | undefined>();
  // v1.16.0: Preset ativo do filtro rápido de Prazo (apenas para destaque visual)
  const [duePreset, setDuePreset] = useState<DueDatePreset>(null);
  const [completedFrom, setCompletedFrom] = useState<Date | undefined>();
  const [completedTo, setCompletedTo] = useState<Date | undefined>();
  const [createdFrom, setCreatedFrom] = useState<Date | undefined>();
  const [createdTo, setCreatedTo] = useState<Date | undefined>();

  // v1.12.0: tenantId para queries de fases
  const tenantId = currentTenant?.id;

  // Realtime centralizado da página: sincroniza listagem entre abas/usuários
  // sem F5. Inclui debounce de 300ms, pausa em background e listener único
  // em `tasks` (ver useTaskListRealtime para detalhes das mitigações).
  useTaskListRealtime(tenantId);


  // v1.12.0: Query para buscar fases filtráveis (todas ou da categoria selecionada)
  // v2.2.0: passa `p_task_type` para respeitar o módulo (Normal/Retenção/Jurídico) —
  // sem isso a RPC assume o módulo padrão e o select de Fase ficava vazio.
  const { data: filterPhases = [] } = useQuery({
    queryKey: ['task-phases-for-filter', tenantId, filters.category_id, taskModule.type],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase.rpc('get_task_phases', {
        p_category_id: filters.category_id || null,
        p_task_type: taskModule.type,
      } as any);
      if (error) throw error;
      return (data || []) as Array<{ id: string; name: string; color: string }>;
    },
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });

  // v2.1.0: Classificações para filtro rápido (filtra por categoria selecionada)
  const { classifications: filterClassifications } = useTaskClassifications(
    filters.category_id || undefined, true
  );

  // v2.2.0: Estados dos filtros exclusivos de Retenção/Jurídico
  const isRetentionOrLegal = taskModule.type === 'retention' || taskModule.type === 'legal';
  const isLegalModule = taskModule.type === 'legal';
  const [requestFrom, setRequestFrom] = useState<Date | undefined>();
  const [requestTo, setRequestTo] = useState<Date | undefined>();
  const [legalDeadlineFrom, setLegalDeadlineFrom] = useState<Date | undefined>();
  const [legalDeadlineTo, setLegalDeadlineTo] = useState<Date | undefined>();

  /** Converte Date → "YYYY-MM-DD" para colunas DATE (sem shift de fuso) */
  const updateDateOnlyFilter = (key: 'request_from' | 'request_to' | 'legal_deadline_from' | 'legal_deadline_to', d: Date | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: d ? format(d, 'yyyy-MM-dd') : undefined,
    }));
  };

  // v1.12.0: Contagem de filtros avançados ativos (para badge do botão "Mais")
  const advancedFiltersCount = [
    completedFrom, completedTo, createdFrom, createdTo, filters.classification_id,
    filters.period, requestFrom, requestTo, legalDeadlineFrom, legalDeadlineTo,
  ].filter(Boolean).length;

  // v2.1.0: Estado do Sheet lateral de filtros avançados
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  /** Helper: verifica se o usuário logado é o responsável pela tarefa */
  const isResponsibleFor = useCallback((task: TaskListItem) => {
    return task.assigned_to === user?.id;
  }, [user?.id]);

  // Estado de fetching (para indicador visual)
  const isFetching = isLoading;

  // Verifica se há filtros ativos (v1.12.0: inclui novos filtros)
  // Inclui também filtros vindos do drill-down do KPI (date_*, completed_*, lockedFilters)
  // para evitar exibir CTA "Crie sua primeira tarefa" quando o modal está apenas filtrado.
  const hasFilters = !!(
    filters.search || filters.category_id || filters.classification_id || filters.status || filters.priority || kpiFilter ||
    filters.phase_id || filters.assigned_to || filters.created_by ||
    filters.date_from || filters.date_to ||
    filters.completed_from || filters.completed_to ||
    filters.due_date_from || filters.due_date_to ||
    filters.due_date_is_null || filters.my_tasks_user_id ||
    dueDateFrom || dueDateTo || completedFrom || completedTo || createdFrom || createdTo ||
    (lockedFilters && Object.keys(lockedFilters).length > 0)
  );

  /**
   * Executa refresh manual dos dados
   */
  const handleRefresh = async () => {
    await triggerRefresh(async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      await queryClient.invalidateQueries({ queryKey: ['task-list-kpis'] });
      // Limpar cache de subtarefas ao refresh
      setSubtasksMap({});
      setExpandedTasks(new Set());
    });
  };

  /**
   * Aplica busca em tempo real (onChange)
   */
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    setFilters(prev => ({ ...prev, search: value || undefined }));
    setPage(1);
  }, []);

  /** Atualiza filtro individual — limpa cache de subtarefas ao mudar filtro */
  const updateFilter = (key: keyof TaskListFilters, value: string | undefined) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      // v2.1.0: Ao trocar categoria, limpar classificação incompatível
      if (key === 'category_id') {
        next.classification_id = undefined;
      }
      return next;
    });
    setPage(1);
    // v1.4.0: Limpar cache de subtarefas ao mudar filtros para evitar dados antigos
    setSubtasksMap({});
    setExpandedTasks(new Set());
  };

  /** Limpa todos os filtros e cache de subtarefas (v1.12.0: inclui novos filtros) */
  /** Em modo drill-down, preserva os lockedFilters (não removíveis) restaurando os iniciais. */
  const clearFilters = () => {
    setFilters({ ...(initialFilters || {}) });
    setSearchInput('');
    setKpiFilter(null);
    setPage(1);
    // v1.12.0: Limpar estados de data
    setDueDateFrom(undefined);
    setDueDateTo(undefined);
    setDuePreset(null);
    setCompletedFrom(undefined);
    setCompletedTo(undefined);
    setCreatedFrom(undefined);
    setCreatedTo(undefined);
    // v1.15.0: Resetar checkbox de concluídas
    setIncludeCompleted(false);
    // v1.4.0: Limpar cache de subtarefas ao limpar filtros
    setSubtasksMap({});
    setExpandedTasks(new Set());
  };

  // ============================================
  // v1.1.0: Helpers de edição em massa
  // ============================================

  /** Ativa/desativa modo de edição em massa */
  const toggleBulkEditMode = useCallback(() => {
    setBulkEditMode(prev => {
      if (prev) setSelectedTaskIds(new Set());
      return !prev;
    });
  }, []);

  /** Sai do modo de edição em massa e limpa seleção */
  const exitBulkEditMode = useCallback(() => {
    setBulkEditMode(false);
    setSelectedTaskIds(new Set());
  }, []);

  /** Toggle seleção de uma tarefa individual */
  const toggleTaskSelection = useCallback((taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  /** Seleciona/deseleciona todas as tarefas visíveis */
  const toggleSelectAll = useCallback(() => {
    setSelectedTaskIds(prev => {
      if (prev.size === tasks.length) {
        return new Set();
      }
      return new Set(tasks.map(t => t.id));
    });
  }, [tasks]);

  /**
   * v1.12.0: Helper para sincronizar datas do filtro avançado para o objeto filters
   */
  const updateDateFilter = (key: keyof TaskListFilters, date: Date | undefined) => {
    const isoStr = date ? date.toISOString() : undefined;
    setFilters(prev => ({ ...prev, [key]: isoStr }));
    setPage(1);
    setSubtasksMap({});
    setExpandedTasks(new Set());
  };

  /**
   * Callback do clique nos KPI cards — filtra por status ou 'created_today'
   * Corrigido: seta date_from/date_to para 'created_today' com cálculo UTC
   * para evitar mismatch de timezone com o banco (CURRENT_DATE em UTC).
   */
  const handleKpiFilter = useCallback((statusKey: string | null) => {
    setKpiFilter(statusKey);
    if (!statusKey) {
      // Limpar filtros injetados pelo KPI
      setFilters(prev => {
        const next = { ...prev };
        delete next.status;
        delete next.date_from;
        delete next.date_to;
        return next;
      });
    } else if (statusKey === 'created_today') {
      // v2 (2026-04-20): janela completa do dia local (00:00 → 23:59) em ISO timestamptz
      // alinhada com a percepção do usuário e com a RPC get_task_list_kpis (que agora
      // também usa fuso America/Sao_Paulo). Antes enviávamos a mesma string "YYYY-MM-DD"
      // em date_from e date_to, fazendo o Postgres colapsar a janela em 1 segundo
      // (midnight UTC) e devolver zero resultados no drill-down.
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      setFilters(prev => {
        const next = { ...prev };
        delete next.status;
        next.date_from = start.toISOString();
        next.date_to = end.toISOString();
        return next;
      });
    } else {
      // Filtro por status (in_progress, paused, completed)
      setFilters(prev => {
        const next = { ...prev, status: statusKey };
        delete next.date_from;
        delete next.date_to;
        return next;
      });
    }
    setPage(1);
    // Limpar cache de subtarefas ao mudar filtro KPI
    setSubtasksMap({});
    setExpandedTasks(new Set());
  }, []);

  /** Alterna ordenação de coluna */
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  /** Abre modal de detalhes */
  const handleViewTask = (task: TaskListItem) => {
    setViewTaskId(task.id);
    setViewModalOpen(true);
  };

  /** Abre dialog de edição */
  const handleEditTask = (task: TaskListItem) => {
    setEditingTaskId(task.id);
    setShowEditDialog(true);
  };

  /** Abre o diálogo "Alterar Fase" (troca de categoria/fase) a partir da listagem */
  const handleChangePhase = (task: TaskListItem) => {
    setPhaseDialogTask(task);
  };
  /**
   * Expande/colapsa subtarefas de uma tarefa pai
   * Lazy load: busca subtarefas via RPC apenas ao expandir
   */
  const toggleExpandTask = async (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (expandedTasks.has(taskId)) {
      // Colapsar
      setExpandedTasks(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      return;
    }

    // Expandir — buscar subtarefas se não estão no cache
    if (!subtasksMap[taskId]) {
      setLoadingSubtasks(prev => new Set(prev).add(taskId));
      try {
        const { data, error } = await supabase.rpc('get_subtasks_for_list', {
          p_task_id: taskId,
          // v1.4.0: Passa filtro de status ativo para alinhar subtarefas com o filtro da listagem
          p_status_filter: filters.status || null,
          p_tenant_hint: null,
        } as any);
        if (error) throw error;
        setSubtasksMap(prev => ({
          ...prev,
          // Cast duplo: a RPC de subtarefas retorna um subconjunto de TaskListItem
          [taskId]: (data || []) as unknown as TaskListItem[],

        }));
      } catch (err: any) {
        logger.error('Erro ao buscar subtarefas', err);
        toast.error('Erro ao carregar subtarefas');
        return;
      } finally {
        setLoadingSubtasks(prev => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        });
      }
    }

    setExpandedTasks(prev => new Set(prev).add(taskId));
  };

  
  /** Altera status da tarefa via RPC */
  const handleChangeStatus = async (taskId: string, newStatus: string) => {
    try {
      const { error } = await supabase.rpc('change_task_status', {
        p_task_id: taskId,
        p_new_status: newStatus as any,
      });
      if (error) throw error;
      toast.success(`Status alterado para "${SHARED_STATUS_LABELS[newStatus] || newStatus}"`);
      // Invalida todas as caches que dependem do status da tarefa para evitar
      // que o modal/kanban exibam estado stale após a ação rápida da listagem.
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-pauses', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
      // Timeline de andamentos: o trigger do DB grava em task_comments na mesma
      // transação, então invalidar a cache evita esperar o Realtime (10–30s).
      queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
    } catch (err: any) {
      logger.error('Erro ao alterar status:', err);
      toast.error(err.message || 'Erro ao alterar status');
    }
  };

  /** Handler de mudança de página */
  const handlePageChange = (newPage: number) => setPage(newPage);

  /** Handler de mudança de tamanho da página */
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  /**
   * Renderiza ícone de ordenação na coluna
   */
  const renderSortIcon = (field: string) => {
    if (sortBy === field) {
      return sortDir === 'asc' 
        ? <ChevronUp className="h-3 w-3" /> 
        : <ChevronDown className="h-3 w-3" />;
    }
    return <ArrowUpDown className="h-3 w-3 opacity-30" />;
  };

  /* formatDue + getDeadlineInfo agora vivem em taskListShared/deadline-utils
     e são consumidos diretamente pelo <TaskListTable />. */

  /* Contexto compartilhado consumido por <TaskListTable /> tanto no modo
     lista quanto dentro de cada grupo de <TaskGroupedView />. */
  const taskListTableContext: TaskListTableContextValue = {
    bulkEditMode,
    selectedTaskIds,
    toggleTaskSelection,
    setSelectedTaskIds,
    sortBy,
    sortDir,
    handleSort,
    expandedTasks,
    loadingSubtasks,
    subtasksMap,
    toggleExpandTask,
    canDoOnPhase,
    isResponsibleFor,
    handleViewTask,
    handleEditTask,
    handleChangeStatus,
    handleChangePhase,
  };

  return (
    <TaskListTableProvider value={taskListTableContext}>
    <div className="flex flex-col h-full">

      {/* ============================================================= */}
      {/* Header Compacto Premium (padrão CrmDeals) — oculto em modo modal */}
      {/* ============================================================= */}
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
          {/* Esquerda: Título + (no mobile) indicadores de uso à direita na mesma linha */}
          <div className="flex items-center justify-between gap-2 sm:block">
            <div>
              <h1 className="text-xl font-semibold">{title || taskModule.label}</h1>
              {/* Subtítulo oculto em mobile para economizar espaço vertical */}
              <p className="hidden sm:block text-xs text-muted-foreground">
                {subtitle || taskModule.subtitle}
              </p>
            </div>
            {/* Mobile: indicadores de uso + atalhos refresh/nova tarefa ao lado do título */}
            <div className="flex items-center gap-1.5 sm:hidden">
              {!hideKpiCards && <TasksUsageInline />}
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing || isFetching}
                title="Atualizar dados"
              >
                <RefreshCw className={cn("h-3.5 w-3.5", (isRefreshing || isFetching) && "animate-spin")} />
              </Button>
              {canCreate(moduleKeys.list) && canCreateInAnyPhase && (
                <Button
                  size="icon-sm"
                  onClick={() => setShowCreateDialog(true)}
                  title={taskModule.newLabel}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          {/* Centro: Input de busca premium */}
          <div className="relative flex-1 max-w-md mx-4 hidden md:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <Input
              placeholder={`Buscar ${taskModule.singular.toLowerCase()} por título ou número...`}
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="h-10 pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 transition-all duration-200 focus-visible:bg-background focus-visible:shadow-md focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
            />
          </div>

          {/* Direita: indicadores de uso (acima — apenas desktop) + botões de ação (abaixo) */}
          <div className="flex flex-col items-end gap-1.5">
            {/* Em mobile os indicadores já aparecem ao lado do título; aqui apenas desktop */}
            {!hideKpiCards && (
              <div className="hidden sm:block">
                <TasksUsageInline />
              </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-1.5">

            {/* v1.1.0: Botão de edição em massa (condicionado por permissão) */}
            {canBulkEdit && (
              <Button
                variant={bulkEditMode ? 'default' : 'outline'}
                size="icon-sm"
                onClick={toggleBulkEditMode}
                title={bulkEditMode ? 'Sair do modo edição em massa' : 'Editar em massa'}
              >
                <ListChecks className="h-3.5 w-3.5" />
              </Button>
            )}
            {/* v3.0.0: Toggle de visualização — Lista x Agrupada (oculto quando forçado lista) */}
            {!forceListView && (
              <div className="flex items-center rounded-md border border-input bg-background overflow-hidden">
                <Button
                  type="button"
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="icon-sm"
                  onClick={() => setViewMode('list')}
                  title="Visualizar como lista"
                  className="rounded-none h-7 w-7"
                >
                  <LayoutList className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant={viewMode === 'grouped' ? 'default' : 'ghost'}
                  size="icon-sm"
                  onClick={() => setViewMode('grouped')}
                  title="Visualizar agrupado por campo"
                  className="rounded-none h-7 w-7"
                >
                  <Rows3 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

            {/* v3.0.0: Seleção do campo de agrupamento (só aparece no modo agrupado) */}
            {!forceListView && viewMode === 'grouped' && (
              <Select value={groupBy} onValueChange={(v) => setGroupBy(v as TaskGroupBy)}>
                <SelectTrigger className="h-7 w-[140px] sm:w-[160px] text-xs">
                  <SelectValue placeholder="Agrupar por..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phase_id">Agrupar por Fase</SelectItem>
                  <SelectItem value="status">Agrupar por Status</SelectItem>
                  <SelectItem value="priority">Agrupar por Prioridade</SelectItem>
                  <SelectItem value="assigned_to">Agrupar por Responsável</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Botão Expandir/Colapsar todos os grupos (preferência persistida) */}
            {!forceListView && viewMode === 'grouped' && (
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => {
                  const next = groupsDefault === 'expanded' ? 'collapsed' : 'expanded';
                  setGroupsDefault(next);
                  setExpandAllSignal((n) => n + 1);
                }}
                title={
                  groupsDefault === 'expanded'
                    ? 'Colapsar todos os grupos'
                    : 'Expandir todos os grupos'
                }
              >
                {groupsDefault === 'expanded' ? (
                  <ChevronsDownUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                )}
              </Button>
            )}

            {/* Botão atalho para Kanban */}
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => navigate(`${taskModule.basePath}/kanban`)}
              title="Alternar para Kanban"
            >
              <Kanban className="h-3.5 w-3.5" />
            </Button>
            {/* Botão refresh — oculto em mobile (já aparece ao lado do título) */}
            <Button
              variant="outline"
              size="icon-sm"
              onClick={handleRefresh}
              disabled={isRefreshing || isFetching}
              title="Atualizar dados"
              className="hidden sm:inline-flex"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", (isRefreshing || isFetching) && "animate-spin")} />
            </Button>
            {canCreate(moduleKeys.list) && canCreateInAnyPhase && (
              <Button
                size="xs"
                onClick={() => setShowCreateDialog(true)}
                title={taskModule.newLabel}
                className="hidden sm:inline-flex hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <Plus className="sm:mr-1.5" />
                <span className="hidden sm:inline">{taskModule.newLabel}</span>
              </Button>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Card de Capacidade antigo removido — agora exibido inline no header acima dos botões */}

      {/* ============================================================= */}
      {/* KPI Cards Totalizadores (server-side com permissões) — opcional */}
      {/* ============================================================= */}
      {!hideKpiCards && (
        <TaskListKpiCards
          kpis={kpis}
          loading={kpisLoading}
          activeStatus={kpiFilter || undefined}
          onFilterByStatus={handleKpiFilter}
        />
      )}

      {/* ============================================================= */}
      {/* Barra de Filtros Premium (padrão CrmDeals) */}
      {/* ============================================================= */}
      <div className="flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30">
        {/* Busca mobile (visível apenas em telas pequenas) */}
        <div className="relative flex-1 min-w-[200px] max-w-sm md:hidden">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground/60" />
          <Input
            placeholder="Buscar tarefa..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8 h-6 text-[11px] sm:h-8 sm:text-sm rounded-lg bg-muted/40 border-none"
          />
        </div>

        {/* Categoria */}
        <Select
          value={filters.category_id || 'all'}
          onValueChange={(v) => updateFilter('category_id', v === 'all' ? undefined : v)}
        >
          <SelectTrigger className="w-[100px] h-6 text-[11px] sm:w-[140px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5">
              <ClipboardList className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <SelectValue placeholder="Categoria" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Categoria</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                  {cat.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>


        {/* Status */}
        <Select
          value={filters.status || 'all'}
          onValueChange={(v) => updateFilter('status', v === 'all' ? undefined : v)}
        >
          <SelectTrigger className="w-[95px] h-6 text-[11px] sm:w-[130px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5">
              <PlayCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <SelectValue placeholder="Status" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Status</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="in_progress">Em andamento</SelectItem>
            <SelectItem value="paused">Pausada</SelectItem>
            <SelectItem value="completed">Concluída</SelectItem>
            <SelectItem value="cancelled">Cancelada</SelectItem>
          </SelectContent>
        </Select>

        {/* Prioridade */}
        <Select
          value={filters.priority || 'all'}
          onValueChange={(v) => updateFilter('priority', v === 'all' ? undefined : v)}
        >
          <SelectTrigger className="w-[95px] h-6 text-[11px] sm:w-[130px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <SelectValue placeholder="Prioridade" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Prioridade</SelectItem>
            {(['urgent', 'high', 'normal', 'low'] as const).map((p) => (
              <SelectItem key={p} value={p}>
                <span className="flex items-center gap-2">
                  <PriorityFlag priority={p} size={13} />
                  {PRIORITY_LABELS[p]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* v1.12.0: Fase */}
        <Select
          value={filters.phase_id || 'all'}
          onValueChange={(v) => updateFilter('phase_id', v === 'all' ? undefined : v)}
        >
          <SelectTrigger className="w-[95px] h-6 text-[11px] sm:w-[130px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5">
              <Filter className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <SelectValue placeholder="Fase" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Fase</SelectItem>
            {filterPhases.map(phase => (
              <SelectItem key={phase.id} value={phase.id}>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: phase.color }} />
                  {phase.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* v1.12.0: Responsável (padrão CrmUserFilter com busca e avatar) */}
        <CrmUserFilter
          value={filters.assigned_to || null}
          onChange={(id) => updateFilter('assigned_to', id || undefined)}
          label="Responsável"
          searchPlaceholder="Buscar responsável..."
          allLabel="Todos Responsáveis"
          groupLabel="Responsáveis"
          className="h-6 text-[11px] sm:h-8 sm:text-sm"
        />

        {/* v1.12.0: Criado por (padrão CrmUserFilter com busca e avatar) */}
        <CrmUserFilter
          value={filters.created_by || null}
          onChange={(id) => updateFilter('created_by', id || undefined)}
          label="Criado por"
          searchPlaceholder="Buscar criador..."
          allLabel="Todos Criadores"
          groupLabel="Criadores"
          className="h-6 text-[11px] sm:h-8 sm:text-sm"
        />

        {/* v1.16.0: Filtro rápido de Prazo (presets + personalizada + sem prazo) */}
        <DueDateQuickFilter
          from={dueDateFrom}
          to={dueDateTo}
          noDueDate={!!filters.due_date_is_null}
          preset={duePreset}
          triggerClassName="h-6 px-2 text-[10px] sm:h-8 sm:px-3 sm:text-xs border-0 bg-background rounded-full"
          onChange={({ from, to, noDueDate, preset }) => {
            setDueDateFrom(from);
            setDueDateTo(to);
            setDuePreset(preset);
            // Atualiza filtros server-side em uma única passada (evita corrida de setState)
            setFilters(prev => ({
              ...prev,
              due_date_from: from ? from.toISOString() : undefined,
              due_date_to: to ? to.toISOString() : undefined,
              due_date_is_null: noDueDate ? true : undefined,
            }));
            setPage(1);
          }}
        />

        {/* v2.1.0: Botão "Mais" com Sheet lateral (padrão FinancialCharges) */}
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-[10px] gap-1.5 sm:h-8 sm:px-3 sm:text-xs border-0 bg-background rounded-full"
          onClick={() => setIsMoreFiltersOpen(true)}
        >
          <SlidersHorizontal className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          Mais
          {advancedFiltersCount > 0 && (
            <Badge variant="secondary" className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0">
              {advancedFiltersCount}
            </Badge>
          )}
        </Button>
        <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
          <SheetContent side="right" className="w-80 p-0 flex flex-col">
            <SheetHeader className="p-4 border-b">
              <div className="flex items-center justify-between">
                <SheetTitle className="text-sm">Filtros Avançados</SheetTitle>
                {advancedFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={() => {
                      setCompletedFrom(undefined); setCompletedTo(undefined);
                      updateDateFilter('completed_from', undefined); updateDateFilter('completed_to', undefined);
                      setCreatedFrom(undefined); setCreatedTo(undefined);
                      updateDateFilter('date_from', undefined); updateDateFilter('date_to', undefined);
                      updateFilter('classification_id', undefined);
                      // v2.2.0 — filtros de Retenção/Jurídico
                      setRequestFrom(undefined); setRequestTo(undefined);
                      setLegalDeadlineFrom(undefined); setLegalDeadlineTo(undefined);
                      setFilters(prev => ({
                        ...prev,
                        period: undefined,
                        request_from: undefined,
                        request_to: undefined,
                        legal_deadline_from: undefined,
                        legal_deadline_to: undefined,
                      }));
                    }}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Limpar
                  </Button>
                )}
              </div>
            </SheetHeader>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {/* Classificação — movido da toolbar */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-foreground flex items-center gap-1.5">
                    <Tag className="h-3 w-3" />
                    Classificação
                  </Label>
                  <Select
                    value={filters.classification_id || 'all'}
                    onValueChange={(v) => updateFilter('classification_id', v === 'all' ? undefined : v)}
                  >
                    <SelectTrigger className="h-8 text-sm w-full">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[280px] w-[336px]">
                      <SelectItem value="all">Todas</SelectItem>
                      {filterClassifications.map(cls => (
                        <SelectItem key={cls.id} value={cls.id}>
                          <div className="flex items-center gap-1.5 min-w-0">
                            {cls.parent_id && <span className="text-muted-foreground ml-1 shrink-0">└─</span>}
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cls.color }} />
                            <span className="truncate max-w-[260px] inline-block">{cls.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Data Conclusão */}
                <DateFilterRow
                  label="Data Conclusão"
                  fromDate={completedFrom}
                  toDate={completedTo}
                  onFromChange={(d) => { setCompletedFrom(d); updateDateFilter('completed_from', d); }}
                  onToChange={(d) => { setCompletedTo(d); updateDateFilter('completed_to', d); }}
                />

                {/* Data Criação */}
                <DateFilterRow
                  label="Data Criação"
                  fromDate={createdFrom}
                  toDate={createdTo}
                  onFromChange={(d) => { setCreatedFrom(d); updateDateFilter('date_from', d); }}
                  onToChange={(d) => { setCreatedTo(d); updateDateFilter('date_to', d); }}
                />

                {/* v2.2.0 — Filtros exclusivos de Retenção/Jurídico */}
                {isRetentionOrLegal && (
                  <>
                    {/* Período consumido desde a solicitação/entrada */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-foreground flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        Período
                      </Label>
                      <Select
                        value={filters.period || 'all'}
                        onValueChange={(v) =>
                          setFilters(prev => ({ ...prev, period: v === 'all' ? undefined : v }))
                        }
                      >
                        <SelectTrigger className="h-8 text-sm w-full">
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          {RETENTION_PERIOD_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Data de solicitação (Retenção) / Data de entrada (Jurídico) */}
                    <DateFilterRow
                      label={isLegalModule ? 'Data de Entrada' : 'Data Solicitação'}
                      fromDate={requestFrom}
                      toDate={requestTo}
                      onFromChange={(d) => { setRequestFrom(d); updateDateOnlyFilter('request_from', d); }}
                      onToChange={(d) => { setRequestTo(d); updateDateOnlyFilter('request_to', d); }}
                    />

                    {/* Prazo processual — exclusivo do Jurídico */}
                    {isLegalModule && (
                      <DateFilterRow
                        label="Prazo Processual"
                        fromDate={legalDeadlineFrom}
                        toDate={legalDeadlineTo}
                        onFromChange={(d) => { setLegalDeadlineFrom(d); updateDateOnlyFilter('legal_deadline_from', d); }}
                        onToChange={(d) => { setLegalDeadlineTo(d); updateDateOnlyFilter('legal_deadline_to', d); }}
                      />
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* Botão Limpar Filtros */}
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-6 px-2 text-[10px] sm:h-8 sm:text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}

        {/* v1.15.0: Checkbox "Incluir finalizadas" */}
        <div className="flex items-center gap-1.5 ml-auto">
          <Checkbox
            id="include-completed"
            checked={includeCompleted}
            onCheckedChange={(checked) => {
              setIncludeCompleted(!!checked);
              setPage(1);
            }}
            className="h-3 w-3 sm:h-3.5 sm:w-3.5"
          />
          <label
            htmlFor="include-completed"
            className="text-[10px] sm:text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap"
          >
            Incluir finalizadas
          </label>
        </div>

        {/* Indicador de carregamento inline */}
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* v1.1.0: Barra de edição em massa */}
      {bulkEditMode && selectedTaskIds.size > 0 && (
        <TaskBulkEditBar
          selectedCount={selectedTaskIds.size}
          selectedTaskIds={Array.from(selectedTaskIds)}
          onCancel={exitBulkEditMode}
          onSuccess={exitBulkEditMode}
        />
      )}

      {/* ============================================================= */}
      {/* Tabela Premium (padrão CrmDeals) */}
      {/* ============================================================= */}
      <div className="flex-1 overflow-auto">
        {viewMode === 'grouped' ? (
          /* v3.0.0: Visualização agrupada (reaproveita filtros, RPC e modais) */
          <TaskGroupedView
            baseFilters={mergedFilters}
            groupBy={groupBy}
            sortBy={sortBy}
            sortDir={sortDir}
            defaultExpanded={groupsDefault === 'expanded'}
            expandAllSignal={expandAllSignal}
            expandAllValue={groupsDefault === 'expanded'}
            onViewTask={handleViewTask}
            onEditTask={handleEditTask}
            canDoOnPhase={canDoOnPhase}
            isResponsibleFor={isResponsibleFor}
          />
        ) : isLoading ? (
          /* Skeleton loading (padrão CrmDeals) */
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : tasks.length === 0 ? (
          /* Estado vazio premium (padrão CrmDeals) */
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <ClipboardList className="h-12 w-12 text-muted-foreground/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">Nenhuma tarefa encontrada</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {hasFilters
                  ? 'Tente ajustar os filtros de busca'
                  : taskModule.emptyHint}
              </p>
            </div>
            {!hasFilters && canCreate(moduleKeys.list) && canCreateInAnyPhase && (
              <Button 
                size="xs"
                onClick={() => setShowCreateDialog(true)}
                className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <Plus className="mr-1.5" />
                {taskModule.newLabel}
              </Button>
            )}
          </div>
        ) : (
          <ScrollIndicators className="h-full w-full">
            <div className="h-full w-full overflow-hidden">
              <TaskListTable tasks={tasks} />
            </div>
          </ScrollIndicators>
        )}
      </div>

      {/* ============================================================= */}
      {/* Footer de Paginação (padrão CrmDeals) */}
      {/* ============================================================= */}
      {viewMode === 'list' && !isLoading && tasks.length > 0 && (
        <div className="border-t px-4 py-2 bg-background">
          <PaginationFooter
            totalItems={totalRecords}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </div>
      )}

      {/* Dialog de criação de tarefa */}
      <TaskFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onLimitReached={() => setLimitModalOpen(true)}
        onSuccess={(taskId, taskNumber) => {
          setShowCreateDialog(false);
          queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
          queryClient.invalidateQueries({ queryKey: ['tasks-usage'] });
          // Exibir modal de sucesso com número da tarefa
          if (taskId && taskNumber) {
            setCreatedTaskId(taskId);
            setCreatedTaskNumber(taskNumber);
            setShowSuccessDialog(true);
          }
        }}
      />

      {/* Modal de limite mensal de tarefas atingido (disparado pelo trigger do banco) */}
      <LimitReachedModal
        open={limitModalOpen}
        onOpenChange={setLimitModalOpen}
        resourceType="message"
        currentCount={tasksUsage?.tasks_created_month}
        maxAllowed={tasksUsage?.tasks_limit}
        customMessage="Você atingiu o limite mensal de tarefas do seu plano. Contrate um pack para continuar criando tarefas neste mês."
      />
      {/* Modal de sucesso após criação */}
      <TaskCreatedSuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        taskNumber={createdTaskNumber}
        onViewTask={() => {
          setShowSuccessDialog(false);
          if (createdTaskId) {
            setViewTaskId(createdTaskId);
            setViewModalOpen(true);
          }
        }}
      />

      {/* Dialog de edição de tarefa */}
      {editingTaskId && (
        <TaskFormDialog
          open={showEditDialog}
          onOpenChange={(open) => {
            setShowEditDialog(open);
            if (!open) setEditingTaskId(null);
          }}
          editTaskId={editingTaskId}
          onSuccess={() => {
            setShowEditDialog(false);
            setEditingTaskId(null);
            queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
          }}
        />
      )}

      {/* Modal Premium de Detalhes */}
      <TaskDetailsModal
        taskId={viewTaskId}
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
      />

      {/* Diálogo "Alterar Fase" (atalho da listagem — Tarefas/Retenção/Jurídico) */}
      {phaseDialogTask && (
        <TaskChangePhaseDialog
          open
          onOpenChange={(open) => !open && setPhaseDialogTask(null)}
          taskId={phaseDialogTask.id}
          taskType={taskModule.type}
          currentCategoryId={phaseDialogTask.category_id ?? null}
          currentPhaseId={phaseDialogTask.phase_id ?? null}
          currentPhaseName={phaseDialogTask.phase_name ?? null}
          currentPhaseColor={phaseDialogTask.phase_color ?? null}
          onSuccess={() => {
            // Atualiza listagem e detalhes sem esperar o Realtime
            queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
            queryClient.invalidateQueries({ queryKey: ['task-details', phaseDialogTask.id] });
            queryClient.invalidateQueries({ queryKey: ['task-comments', phaseDialogTask.id] });
            queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
            queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
          }}
        />
      )}
    </div>
    </TaskListTableProvider>
  );
}
